import React, { createContext, useContext, useState, useCallback } from 'react';
import en from './en.json';
import th from './th.json';

const translations = { en, th };

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [locale, setLocale] = useState(() => {
    const saved = localStorage.getItem('locale');
    if (saved && translations[saved]) return saved;
    // Try to detect browser language
    const browserLang = navigator.language.split('-')[0];
    return translations[browserLang] ? browserLang : 'en';
  });

  const t = useCallback((key, params = {}) => {
    const keys = key.split('.');
    let value = translations[locale];

    for (const k of keys) {
      value = value?.[k];
    }

    if (typeof value !== 'string') {
      // Fallback to English
      value = translations.en;
      for (const k of keys) {
        value = value?.[k];
      }
    }

    if (typeof value !== 'string') {
      // Use custom fallback if provided, otherwise return key
      if (params.fallback) {
        return params.fallback;
      }
      console.warn(`Missing translation: ${key}`);
      return key;
    }

    // Replace params like {name} with actual values (except 'fallback')
    return value.replace(/\{(\w+)\}/g, (_, param) => {
      if (param === 'fallback') return `{${param}}`;
      return params[param] ?? `{${param}}`;
    });
  }, [locale]);

  const changeLocale = useCallback((newLocale) => {
    if (translations[newLocale]) {
      setLocale(newLocale);
      localStorage.setItem('locale', newLocale);
    }
  }, []);

  const value = {
    locale,
    setLocale: changeLocale,
    t,
    availableLocales: Object.keys(translations)
  };

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
}
